import * as Sentry from '@sentry/react'
import { scrubInPlace } from '@lib/helpers/scrub'

/**
 * Browser error reporting to Sentry, on only when the build had
 * `VITE_SENTRY_DSN`. Errors only: no performance tracing and no session
 * replay. No user info, cookies, bodies, or query strings; every string in an
 * event or breadcrumb (URLs included, so signing-link tokens) is scrubbed
 * before it's sent.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim()
  if (!dsn) return
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    tracesSampleRate: 0,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
    },
    initialScope: { tags: { app: 'web' } },
    beforeSend(event) {
      scrubInPlace(event)
      return event
    },
    beforeBreadcrumb(breadcrumb) {
      scrubInPlace(breadcrumb)
      return breadcrumb
    },
  })
}

/** Reports an error a route rendered instead of its page (a no-op when Sentry is off). */
export function reportRouteError<E>(error: E): void {
  Sentry.captureException(error, { tags: { source: 'route' } })
}

const reactErrorHandler = Sentry.reactErrorHandler()

/** React's root-level handler for errors no boundary caught (`createRoot`'s `onUncaughtError`). */
export function reportUncaughtError<E>(error: E, info: { componentStack?: string | undefined }): void {
  reactErrorHandler(error, { componentStack: info.componentStack ?? null })
}
